import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PricingEvent } from '@/store/eventsSlice';
import { addQuarterlyEvent, versionToQuarter } from '@/utils/quarterlyEventsHelper';
import { toast } from 'sonner';

interface ConditionalFlags {
  plansChanged: boolean;
  priceIncreased: boolean;
  priceDecreased: boolean;
  priceAvailbilityChanged: boolean;
  introDiscount: boolean;
  displayChanged: boolean;
}

interface ConditionalApprovalDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  event: PricingEvent | null;
  isEditing?: boolean;
  initialFlags?: ConditionalFlags;
  onSuccess: () => void;
}

const ConditionalApprovalDialog = ({
  isOpen,
  onOpenChange,
  event,
  isEditing = false,
  initialFlags,
  onSuccess,
}: ConditionalApprovalDialogProps) => {
  const [conditionalFlags, setConditionalFlags] = useState<ConditionalFlags>({
    plansChanged: false,
    priceIncreased: false,
    priceDecreased: false,
    priceAvailbilityChanged: false,
    introDiscount: false,
    displayChanged: false
  });

  // Update flags when initialFlags changes or modal opens
  useEffect(() => {
    if (initialFlags && isOpen) {
      console.log('🔄 [ConditionalApprovalDialog] Setting initial flags:', initialFlags);
      setConditionalFlags({
        plansChanged: initialFlags.plansChanged ?? false,
        priceIncreased: initialFlags.priceIncreased ?? false,
        priceDecreased: initialFlags.priceDecreased ?? false,
        priceAvailbilityChanged: initialFlags.priceAvailbilityChanged ?? false,
        introDiscount: initialFlags.introDiscount ?? false,
        displayChanged: initialFlags.displayChanged ?? false
      });
    }
  }, [initialFlags, isOpen]);

  const handleConditionalApprove = async () => {
    if (!event) return;

    try {
      console.log('🔄 [ConditionalApprovalDialog] Conditionally approving event:', event.slug);
      const quarter = versionToQuarter(event.v1);

      await addQuarterlyEvent(
        event.slug,
        quarter,
        {
          v1: event.v1,
          v2: event.v2,
          has_changes: true,
          plans_changed: conditionalFlags.plansChanged,
          price_increased: conditionalFlags.priceIncreased,
          price_decreased: conditionalFlags.priceDecreased,
          price_availability_changed: conditionalFlags.priceAvailbilityChanged,
          display_changed: conditionalFlags.displayChanged,
          intro_discount: conditionalFlags.introDiscount,
          changes: event.changes,
          v1_url: event.beforeUrl,
          v2_url: event.afterUrl,
          v1_pi_url: event.beforeUrl || '',
          v2_pi_url: event.afterUrl || ''
        },
        true
      );

      toast.success(isEditing ? 'Event updated successfully' : 'Event conditionally approved');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('❌ [ConditionalApprovalDialog] Error approving event:', error);
      toast.error('Failed to approve event');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Approval" : "Conditional Approval"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="plansChanged"
              checked={conditionalFlags.plansChanged}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, plansChanged: checked === true }))
              }
            />
            <label htmlFor="plansChanged">Plans Changed</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="priceIncreased"
              checked={conditionalFlags.priceIncreased}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, priceIncreased: checked === true }))
              }
            />
            <label htmlFor="priceIncreased">Price Increased</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="priceDecreased"
              checked={conditionalFlags.priceDecreased}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, priceDecreased: checked === true }))
              }
            />
            <label htmlFor="priceDecreased">Price Decreased</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="priceAvailbilityChanged"
              checked={conditionalFlags.priceAvailbilityChanged}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, priceAvailbilityChanged: checked === true }))
              }
            />
            <label htmlFor="priceAvailbilityChanged">Availability Changed</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="introDiscount"
              checked={conditionalFlags.introDiscount}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, introDiscount: checked === true }))
              }
            />
            <label htmlFor="introDiscount">Intro Discount</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="displayChanged"
              checked={conditionalFlags.displayChanged}
              onCheckedChange={(checked) => 
                setConditionalFlags(prev => ({ ...prev, displayChanged: checked === true }))
              }
            />
            <label htmlFor="displayChanged">Display Changed</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConditionalApprove}>
            {isEditing ? "Save Changes" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConditionalApprovalDialog;